import { Injectable } from '@angular/core';
import { Observable } from 'rxjs/Observable';
import { Subject } from 'rxjs/Subject';
import 'rxjs/add/observable/forkJoin';
import 'rxjs/add/operator/map';

import {
  SkillDepartmentLearningProgressResponse,
  SkillSpecializationListItem,
  SkillSpecializationResponse,
  SkillProgressResponse,
  SkillTrainingProgressResponse,
  AiChatMessage,
  AiChatMessageResponse,
  SkillTestGenerateResponse,
  SkillTestResultResponse,
  SkillPracticeTaskResponse,
  SkillLearningGeneralSettingsResponse,
} from '../models/skill-learning.model';
import { SkillSpecializationApiService } from './skill-specialization-api.service';
import { SkillProgressApiService } from './skill-progress-api.service';
import { SkillChatApiService } from './skill-chat-api.service';
import { SkillTestApiService } from './skill-test-api.service';
import { SkillPracticeApiService } from './skill-practice-api.service';
import { SkillSettingsApiService } from './skill-settings-api.service';

export interface LearningTreeSpecialization {
  id: string;
  name: string;
  description: string;
  completedLevels: number;
  totalLevels: number;
  completed: boolean;
}

export interface GroupedSpecialization {
  id: string;
  name: string;
  group: {
    id: string;
    name: string;
  };
  completedTrainings: number;
  totalTrainings: number;
  specializations: LearningTreeSpecialization[];
}

@Injectable()
export class LearningFacadeService {

  private progressLoaded$ = new Subject<SkillProgressResponse>();

  readonly onProgressLoaded$: Observable<SkillProgressResponse> = this.progressLoaded$.asObservable();

  constructor(
    private specializationApi: SkillSpecializationApiService,
    private progressApi: SkillProgressApiService,
    private chatApi: SkillChatApiService,
    private testApi: SkillTestApiService,
    private practiceApi: SkillPracticeApiService,
    private settingsApi: SkillSettingsApiService,
  ) {}

  fetchSpecializationsGrouped(): Observable<GroupedSpecialization[]> {
    return Observable
      .forkJoin(
        this.progressApi.fetchDepartmentLearningProgress(),
        this.specializationApi.fetchAvailableSpecializations(),
      )
      .map(result => this.mapLearningTreeGroups(result[0], result[1]));
  }

  fetchSpecializationDetails(id: string): Observable<SkillSpecializationResponse> {
    return this.specializationApi.fetchSpecializationById(id);
  }

  fetchUserProgress(specializationId: string): Observable<SkillProgressResponse> {
    return this.progressApi.fetchUserProgress(specializationId)
      .do(progress => this.progressLoaded$.next(progress));
  }

  fetchGeneralSettings(): Observable<SkillLearningGeneralSettingsResponse> {
    return this.settingsApi.fetchGeneralSettings();
  }

  toggleTopic(specializationId: string, topicIndex: number): Observable<void> {
    return this.progressApi.toggleTopic(specializationId, topicIndex);
  }

  fetchChatHistory(specializationId: string): Observable<AiChatMessage[]> {
    return this.chatApi.fetchChatHistory(specializationId);
  }

  sendChatMessage(specializationId: string, message: string): Observable<AiChatMessageResponse> {
    const request = { message };
    return this.chatApi.sendMessage(specializationId, request);
  }

  clearChat(specializationId: string): Observable<void> {
    return this.chatApi.clearChat(specializationId);
  }

  generateTest(specializationId: string): Observable<SkillTestGenerateResponse> {
    return this.testApi.generateTest(specializationId);
  }

  submitTest(specializationId: string, answers: { [questionIndex: string]: number }): Observable<SkillTestResultResponse> {
    const request = { answers };
    return this.testApi.submitTest(specializationId, request);
  }

  getTestCooldown(specializationId: string): Observable<number> {
    return this.testApi.fetchTestCooldownSeconds(specializationId);
  }

  startPracticeLevel(specializationId: string, level: number): Observable<SkillPracticeTaskResponse> {
    return this.practiceApi.startPracticeLevel(specializationId, level);
  }

  getPracticeTaskStatus(specializationId: string, level: number): Observable<SkillPracticeTaskResponse> {
    return this.practiceApi.fetchPracticeTask(specializationId, level);
  }

  buildDocumentDownloadUrl(specializationId: string, level: number): string {
    return this.specializationApi.buildLevelDocumentDownloadUrl(specializationId, level);
  }

  resolveCurrentLevel(progress: SkillProgressResponse): number {
    if (!progress) {
      return 0;
    }
    return progress.currentLevel || 0;
  }

  isLevel1Passed(progress: SkillProgressResponse): boolean {
    return progress && progress.currentLevel >= 1;
  }

  isLevel1SelfTestPassed(progress: SkillProgressResponse): boolean {
    return !!progress && (!!progress.level1SelfTestPassed || !!progress.testPassedAt);
  }

  isLevel2Passed(progress: SkillProgressResponse): boolean {
    return progress && progress.currentLevel >= 2;
  }

  private mapLearningTreeGroups(
    progress: SkillDepartmentLearningProgressResponse,
    specializations: SkillSpecializationListItem[],
  ): GroupedSpecialization[] {
    const descriptionMap = this.buildDescriptionMap(specializations);
    return progress.departments.map(department => ({
      id: department.groupId,
      name: department.groupName,
      group: {
        id: department.groupId,
        name: department.groupName,
      },
      completedTrainings: department.completedTrainings,
      totalTrainings: department.totalTrainings,
      specializations: department.trainings.map(training =>
        this.mapLearningTreeSpecialization(training, descriptionMap)),
    }));
  }

  private buildDescriptionMap(specializations: SkillSpecializationListItem[]): Map<string, string> {
    const descriptionMap = new Map<string, string>();
    specializations.forEach(spec => descriptionMap.set(spec.id, spec.description || ''));
    return descriptionMap;
  }

  private mapLearningTreeSpecialization(
    training: SkillTrainingProgressResponse,
    descriptionMap: Map<string, string>,
  ): LearningTreeSpecialization {
    return {
      id: training.specializationId,
      name: training.specializationName,
      description: descriptionMap.get(training.specializationId) || '',
      completedLevels: training.completedLevels,
      totalLevels: training.totalLevels,
      completed: training.completed,
    };
  }
}



// WEBPACK FOOTER //
// C:/work/internal-frontend/src/app/content-page/common/skill-learning/services/learning-facade.service.ts